import type { GridApiPremium, GridDataSource, GridSortModel } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import {
  getRowSelectionModelFromIds,
  getSelectedRowIdsFromModel,
  GRID_DEFAULT_PAGE_SIZE,
} from "@entrilia/shared/components/grid/gridHelper";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";
import { logError } from "@entrilia/shared/logging";
import type { WorkbookTemplate } from "../../../../api/types/workbookRuns.types";
import storage from "../../../../storage/storage";
import { useFilterContext } from "../../../common/filters/FilterContext";
import { sortAction } from "../../../common/filters/filterState";
import { getCheckedGridSx } from "../../../common/grid/gridStyles";
import { getColumnDefinitions } from "./workbookTemplatesGridDataProvider";
import WorkbookTemplatesToolbarActions from "./WorkbookTemplatesToolbarActions";

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  filterId: string;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  onNewRun: (templateId: string) => void;
  onDeleteTemplates: (ids: string[]) => void;
  getTemplatePath: (templateId: string) => string;
}

const WorkbookTemplatesGrid = ({
  apiRef,
  dataSource,
  filterId,
  selectedIds,
  setSelectedIds,
  onNewRun,
  onDeleteTemplates,
  getTemplatePath,
}: Props) => {
  const { filterState, setFilterState } = useFilterContext<WorkbookTemplate>();
  const { hasAnyPermission } = useClientPermissionsContext();
  const sortModel = useMemo(() => (filterState.sorting ? [filterState.sorting] : []), [filterState.sorting]);
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const columns = useMemo(
    () => getColumnDefinitions({ getTemplatePath, onNewRun, onDeleteTemplates }),
    [getTemplatePath, onNewRun, onDeleteTemplates]
  );

  const handleDataSourceError = useCallback(
    (error: Error) => {
      logError(error, "[WorkbookTemplatesGrid]");
      setDataSourceError(error);
      storage.clearFilterState(filterId);
    },
    [filterId]
  );

  const handleSortModelChange = useCallback(
    (gridSortModel: GridSortModel) => {
      setFilterState(sortAction(gridSortModel));
    },
    [setFilterState]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Failed to load workbook templates" />;
  }

  const canManageReports = hasAnyPermission(["ManageReports"]);

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={canManageReports && selectedIds.length > 0}
      selection={[selectedIds, setSelectedIds]}
      actions={<WorkbookTemplatesToolbarActions selectedIds={selectedIds} onDeleteTemplates={onDeleteTemplates} />}
    >
      <DataGrid<WorkbookTemplate>
        columns={columns}
        apiRef={apiRef}
        dataSource={dataSource}
        lazyLoading
        noRowsText="No templates yet"
        shiftActionsLeft
        disableColumnReorder
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        rowSelectionModel={getRowSelectionModelFromIds(selectedIds)}
        checkboxSelection={canManageReports}
        onRowSelectionModelChange={(selection) => setSelectedIds(getSelectedRowIdsFromModel(selection))}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        sx={getCheckedGridSx}
        paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
        onDataSourceError={handleDataSourceError}
        sortingMode="server"
        disableMultipleColumnsSorting
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default WorkbookTemplatesGrid;
